// Navbar.js
import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { usePageTransition } from "../context/PageTransitionContext";
import { heroPhoto } from "./sections/HeroSection";
import { preloadRoute } from "../utils/preloadAssets";
function MailIcon({ size = 18, strokeWidth = 1.8 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function LinkedinIcon({ size = 18, strokeWidth = 1.8, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect x="2" y="2" width="20" height="20" rx="4" ry="4" />
      <path d="M8 11v5" />
      <path d="M8 8v.01" />
      <path d="M12 16v-5" />
      <path d="M16 16v-3a2 2 0 0 0-4 0" />
    </svg>
  );
}

function GithubIcon({ size = 18, strokeWidth = 1.8 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
      <path d="M9 18c-4.51 2-5-2-7-2" />
    </svg>
  );
}

const Navbar = () => {
  const location = useLocation();
  const { navigateWithTransition } = usePageTransition();
  const isHomePage = location.pathname === "/";

  // 滚动超过视口 60% 时显示小照片（仅首页）
  const [showMiniPhoto, setShowMiniPhoto] = useState(false);
  useEffect(() => {
    if (!isHomePage) {
      setShowMiniPhoto(false);
      return;
    }
    const handleScroll = () => {
      setShowMiniPhoto(window.scrollY > window.innerHeight * 0.6);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  //nav items for every page
  const navItems = {
    "/": ["PROJECTS", "CONTACT"],
    "/about": ["HOME", "PROJECTS", "CONTACT"],
    "/contact": ["HOME", "PROJECTS"],
    "/projects": ["HOME", "CONTACT"],
    // work & works
    "/work": ["HOME", "PROJECTS", "CONTACT"],
    "/work/GreenPlatter": ["HOME", "PROJECTS", "CONTACT"],
    "/work/GreenPlatterApp": ["HOME", "PROJECTS", "CONTACT"],
    "/work/HealthandWellnessTracker": ["HOME", "PROJECTS", "CONTACT"],
  };

  const currentNavItems = navItems[location.pathname] || [];
  const isWorkPage = location.pathname.startsWith("/work");
  const isProjectsPage = location.pathname.startsWith("/projects");
  const isContactPage = location.pathname.startsWith("/contact");

  const bgColors = {
    "/": "bg-lightBeige",
    "/about": "bg-lightBeige",
    "/contact": "bg-midBrown",
    "/projects": "bg-deep",
    // work & works
    "/work": "bg-deep",
    "/work/GreenPlatter": "bg-deep",
    "/work/GreenPlatterApp": "bg-deep",
    "/work/HealthandWellnessTracker": "bg-deep",
  };
  const currentBgColor = bgColors[location.pathname] || "";

  // icon color: light on dark pages, dark on light pages
  const iconColorClass =
    isWorkPage || isProjectsPage
      ? "text-beige hover:text-pink"
      : isContactPage
        ? "text-lightBrown hover:text-pink"
        : "text-midBrown hover:text-lightBrown";

  return (
    <>
      <div
        className={`fixed top-0 left-0 w-full h-10 z-40 ${currentBgColor}`}
      ></div>

      {/* Left social icons */}
      <div className="fixed top-0 left-0 z-50 flex items-center h-10 gap-4 md:gap-8 pl-4">
        <a
          href="mailto:abbieluo.yu@gmail.com"
          className={`transition-colors duration-150 ${iconColorClass}`}
          title="Email"
        >
          <MailIcon size={18} strokeWidth={1.8} />
        </a>
        <a
          href="http://www.linkedin.com/in/abbieluoyu"
          target="_blank"
          rel="noopener noreferrer"
          className={`transition-colors duration-150 ${iconColorClass}`}
          title="LinkedIn"
        >
          <LinkedinIcon size={18} strokeWidth={1.8} />
        </a>
        <a
          href="https://github.com/abbiekuma"
          target="_blank"
          rel="noopener noreferrer"
          className={`transition-colors duration-150 ${iconColorClass}`}
          title="GitHub"
        >
          <GithubIcon size={18} strokeWidth={1.8} />
        </a>
      </div>

      {/* Mini photo — appears in the center of the navbar when scrolled past hero */}
      <AnimatePresence>
        {showMiniPhoto && (
          <motion.div
            key="mini-photo"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="fixed top-1 left-1/2 -translate-x-1/2 z-50 hidden md:block"
          >
            <img
              src={heroPhoto}
              alt="Abbie"
              className="w-8 h-10 object-cover shadow-md"
              style={{ borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%" }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <nav className="fixed flex space-x-4 z-50 right-4 md:space-x-8 top-2">
        {currentNavItems.map((item) => {
          const path =
            item.toLowerCase() === "home" ? "/" : `/${item.toLowerCase()}`;
          return (
            <Link
              key={item}
              to={path === "/" ? "/" : path}
              onMouseEnter={() => preloadRoute(path)}
              onClick={(e) => {
                const onWorkPage = location.pathname.startsWith("/work");
                if (
                  navigateWithTransition &&
                  location.pathname !== path &&
                  !onWorkPage
                ) {
                  e.preventDefault();
                  navigateWithTransition(path);
                }
              }}
              className={`active:text-pink block font-extrabold text-center ${
                location.pathname === "/"
                  ? "text-base md:text-lg text-midBrown hover:text-lightBrown min-w-[60px]"
                  : `text-lg min-w-[70px] ${
                      isWorkPage || isProjectsPage
                        ? "text-beige hover:text-pink"
                        : isContactPage
                          ? "text-lightBrown hover:text-pink"
                          : "hover:text-lightBrown"
                    }`
              }`}
            >
              <motion.span
                className="inline-block"
                whileTap={{ rotate: -3, transition: { duration: 0.15 } }}
              >
                {item}
              </motion.span>
            </Link>
          );
        })}
      </nav>
    </>
  );
};

export default Navbar;
